import React, { useState, useEffect, useMemo } from 'react'
import * as api from '../../services/api.js'
import BookRating, { BookRatingDisplay } from './BookRating.jsx'
import BookDetailPanel, { GenrePicker } from './BookDetailPanel.jsx'
import ReadingSessionModal from './ReadingSessionModal.jsx'
import ReadingChallenge from './ReadingChallenge.jsx'
import ReadingSuggestions from './ReadingSuggestions.jsx'
import BookImport from './BookImport.jsx'
import { SPINE_COLORS } from '../../utils/colours.js'
import AppSelect from '../../components/AppSelect.jsx'
import SectionWelcome from '../../components/SectionWelcome.jsx'
import ConfirmDialog from '../../components/ConfirmDialog.jsx'

const STATUS_ORDER  = ['reading', 'completed', 'wishlist', 'abandoned']
const STATUS_LABELS = { reading: 'Reading', completed: 'Completed', abandoned: 'Abandoned', wishlist: 'Wishlist' }
const TYPE_ICONS    = { physical: '📖', audiobook: '🎧', ebook: '📱' }
const SORT_OPTIONS  = [
  { id: 'added',  label: 'Date added' },
  { id: 'title',  label: 'Title A–Z' },
  { id: 'author', label: 'Author A–Z' },
  { id: 'rating', label: 'Rating' },
]
const TABS = [
  { id: 'library',     label: 'Library' },
  { id: 'challenge',   label: 'Challenge' },
  { id: 'suggestions', label: 'Suggestions' },
  { id: 'reviews',     label: 'Reviews' },
]

// ── Library ───────────────────────────────────────────────────────────────────

const ADD_DEFAULTS = { title: '', author: '', type: 'physical', status: 'reading', pages: '', pagesRead: '', rating: 0, series: '', continues: 0, genres: '', date: '', hours: '', review: '' }
const FL = { fontSize: '0.65rem', color: 'var(--stone)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }

function LibrarySection({ books, onOpenBook, onAddBook, onUpdateBook, onImport }) {
  const [filter,      setFilter]      = useState('all')
  const [sort,        setSort]        = useState('added')
  const [adding,      setAdding]      = useState(false)
  const [moreDetails, setMoreDetails] = useState(false)
  const [form,        setForm]        = useState(ADD_DEFAULTS)
  const [showAll,     setShowAll]     = useState(false)
  const [importOpen,  setImportOpen]  = useState(false)

  const filtered = useMemo(() => {
    let rows = filter === 'all' ? books : books.filter(b => b.status === filter)
    switch (sort) {
      case 'title':  return [...rows].sort((a, b) => a.title.localeCompare(b.title))
      case 'author': return [...rows].sort((a, b) => (a.author || '').localeCompare(b.author || ''))
      case 'rating': return [...rows].sort((a, b) => (b.rating || 0) - (a.rating || 0))
      default:       return rows
    }
  }, [books, filter, sort])

  const shown = showAll ? filtered : filtered.slice(0, 5)

  function cancelAdd() {
    setAdding(false)
    setMoreDetails(false)
    setForm(ADD_DEFAULTS)
  }

  function submitAdd() {
    if (!form.title.trim()) return
    const book = {
      id:         Date.now(),
      title:      form.title.trim(),
      author:     form.author.trim()    || null,
      type:       form.type,
      status:     form.status,
      pages:      parseInt(form.pages)  || 0,
      pages_read: parseInt(form.pagesRead) || 0,
      rating:     form.rating           || 0,
      series:     form.series.trim()    || null,
      continues:  form.continues,
      genres:     form.genres.trim()    || null,
      date:       form.date             || null,
      hours:      parseFloat(form.hours) || null,
      review:     form.review.trim()    || null,
      color:      SPINE_COLORS[books.length % SPINE_COLORS.length],
    }
    onAddBook(book)
    setForm(ADD_DEFAULTS)
    setAdding(false)
    setMoreDetails(false)
  }

  const fs = { fontSize: '0.82rem', padding: '6px 9px', border: '1px solid rgba(0,0,0,0.15)', borderRadius: 8, background: 'var(--bg)', color: 'var(--ink)', fontFamily: 'var(--font-sans)', width: '100%', boxSizing: 'border-box' }

  return (
    <div>
      {/* Controls */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: '1rem' }}>
        <AppSelect
          value={filter}
          onChange={v => { setFilter(v); setShowAll(false) }}
          options={[{ value: 'all', label: 'All books' }, ...STATUS_ORDER.map(s => ({ value: s, label: STATUS_LABELS[s] }))]}
          className="reading-filter-select"
        />
        <AppSelect
          value={sort}
          onChange={setSort}
          options={SORT_OPTIONS.map(o => ({ value: o.id, label: o.label }))}
          className="reading-filter-select"
        />
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <button
            onClick={() => adding ? cancelAdd() : setAdding(true)}
            style={{ background: adding ? 'var(--surf)' : 'var(--accent)', color: adding ? 'var(--ink)' : 'white', border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'var(--font-sans)' }}
          >
            {adding ? 'Cancel' : '+ Add'}
          </button>
          <button
            onClick={() => setImportOpen(true)}
            style={{ background: 'var(--surf)', color: 'var(--ink)', border: '1px solid rgba(0,0,0,0.12)', borderRadius: 8, padding: '6px 12px', fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'var(--font-sans)' }}
          >
            Import
          </button>
        </div>
      </div>

      {/* Add form */}
      {adding && (
        <div style={{ background: 'var(--surf)', borderRadius: 12, padding: '1rem', marginBottom: '1rem' }}>
          {/* Essential fields */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.6rem' }}>
            <input
              placeholder="Title *"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && !moreDetails && submitAdd()}
              style={{ ...fs, gridColumn: '1 / -1' }}
              autoFocus
            />
            <input placeholder="Author" value={form.author} onChange={e => setForm(f => ({ ...f, author: e.target.value }))} style={fs} />
            <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} style={fs}>
              <option value="physical">Physical</option>
              <option value="audiobook">Audiobook</option>
              <option value="ebook">E-book</option>
            </select>
            <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} style={{ ...fs, gridColumn: '1 / -1' }}>
              {STATUS_ORDER.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
            </select>
          </div>

          {/* More details toggle */}
          <button
            type="button"
            onClick={() => setMoreDetails(v => !v)}
            style={{ fontSize: '0.75rem', color: 'var(--stone)', background: 'transparent', border: 'none', padding: '3px 0', cursor: 'pointer', fontFamily: 'var(--font-sans)', marginBottom: moreDetails ? '0.8rem' : 0 }}
          >
            {moreDetails ? '▴ Less details' : '▾ More details'}
          </button>

          {moreDetails && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem', marginBottom: '0.75rem' }}>
              {/* Pages / Hours */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                {form.type !== 'audiobook' ? (
                  <>
                    <div>
                      <div style={FL}>Pages</div>
                      <input type="number" min="0" value={form.pages} onChange={e => setForm(f => ({ ...f, pages: e.target.value }))} style={fs} />
                    </div>
                    <div>
                      <div style={FL}>Pages read</div>
                      <input type="number" min="0" value={form.pagesRead} onChange={e => setForm(f => ({ ...f, pagesRead: e.target.value }))} style={fs} />
                    </div>
                  </>
                ) : (
                  <div>
                    <div style={FL}>Hours</div>
                    <input type="number" min="0" step="0.5" value={form.hours} onChange={e => setForm(f => ({ ...f, hours: e.target.value }))} style={fs} />
                  </div>
                )}
              </div>

              {/* Rating */}
              <div>
                <div style={FL}>Rating</div>
                <BookRating value={form.rating} onChange={r => setForm(f => ({ ...f, rating: r }))} />
              </div>

              {/* Series + want to continue */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                <div style={{ flex: 1 }}>
                  <div style={FL}>Series</div>
                  <input value={form.series} onChange={e => setForm(f => ({ ...f, series: e.target.value }))} placeholder="Series name" style={fs} />
                </div>
                {form.series.trim() && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.78rem', color: 'var(--stone)', cursor: 'pointer', paddingBottom: 7, whiteSpace: 'nowrap' }}>
                    <input type="checkbox" checked={!!form.continues} onChange={e => setForm(f => ({ ...f, continues: e.target.checked ? 1 : 0 }))} />
                    Want to continue
                  </label>
                )}
              </div>

              {/* Genres */}
              <div>
                <div style={FL}>Genres</div>
                <GenrePicker value={form.genres} onChange={v => setForm(f => ({ ...f, genres: v }))} />
              </div>

              {/* Finish date */}
              <div>
                <div style={FL}>Finish date</div>
                <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} style={fs} />
              </div>

              {/* Review */}
              <div>
                <div style={FL}>Review / notes</div>
                <textarea
                  value={form.review}
                  onChange={e => setForm(f => ({ ...f, review: e.target.value }))}
                  placeholder="Your thoughts..."
                  rows={3}
                  style={{ ...fs, resize: 'vertical', fontFamily: 'var(--font-serif)', lineHeight: 1.65 }}
                />
              </div>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: '0.25rem' }}>
            <button
              onClick={cancelAdd}
              style={{ fontSize: '0.78rem', padding: '5px 14px', borderRadius: 8, border: '1px solid rgba(0,0,0,0.15)', background: 'transparent', color: 'var(--stone)', cursor: 'pointer', fontFamily: 'var(--font-sans)' }}
            >
              Cancel
            </button>
            <button
              onClick={submitAdd}
              disabled={!form.title.trim()}
              style={{ background: form.title.trim() ? 'var(--accent)' : 'var(--surf)', color: form.title.trim() ? 'white' : 'var(--stone)', border: 'none', borderRadius: 8, padding: '5px 14px', fontSize: '0.78rem', cursor: form.title.trim() ? 'pointer' : 'default', fontFamily: 'var(--font-sans)', opacity: form.title.trim() ? 1 : 0.5 }}
            >
              Save book
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      {filtered.length === 0 ? (
        <p style={{ fontSize: '0.88rem', color: 'var(--stone)', fontStyle: 'italic', textAlign: 'center', padding: '2rem 0' }}>
          {filter === 'all' ? 'No books yet.' : `No ${STATUS_LABELS[filter].toLowerCase()} books.`}
        </p>
      ) : (
        <>
          <div className="reading-table-wrap">
            <table className="reading-table">
              <thead>
                <tr>
                  <th style={{ width: 5, padding: 0 }} />
                  <th>Title</th>
                  <th className="hide-mobile">Type</th>
                  <th>Status</th>
                  <th className="hide-mobile">Rating</th>
                </tr>
              </thead>
              <tbody>
                {shown.map(book => (
                  <tr key={book.id} className="reading-table-row" onClick={() => onOpenBook(book)}>
                    <td style={{ padding: 0, width: 5 }}>
                      <div style={{ width: 5, height: '100%', minHeight: 36, background: book.color || 'var(--accent)', borderRadius: '4px 0 0 4px' }} />
                    </td>
                    <td className="reading-table-title-cell">
                      <div className="reading-table-title">{book.title}</div>
                      {book.author && <div className="reading-table-author">{book.author}</div>}
                      {book.status === 'reading' && book.pages > 0 && (
                        <div style={{ marginTop: 3, height: 2, background: 'rgba(0,0,0,0.08)', borderRadius: 1, overflow: 'hidden', maxWidth: 100 }}>
                          <div style={{ height: '100%', background: 'var(--accent)', width: `${Math.min(Math.round(((book.pages_read || 0) / book.pages) * 100), 100)}%` }} />
                        </div>
                      )}
                    </td>
                    <td className="hide-mobile" style={{ textAlign: 'center', fontSize: '1rem' }}>
                      {TYPE_ICONS[book.type] || '📖'}
                    </td>
                    <td>
                      <button
                        className={`status-badge status-${book.status}`}
                        onClick={e => { e.stopPropagation(); onUpdateBook(book.id, { status: STATUS_ORDER[(STATUS_ORDER.indexOf(book.status) + 1) % STATUS_ORDER.length] }) }}
                        title="Click to cycle status"
                      >
                        {STATUS_LABELS[book.status]}
                      </button>
                    </td>
                    <td className="hide-mobile">
                      {book.rating > 0 ? <BookRatingDisplay value={book.rating} /> : <span style={{ color: 'var(--stone)', fontSize: '0.72rem' }}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filtered.length > 5 && !showAll && (
            <button className="reading-show-more-btn" onClick={() => setShowAll(true)}>
              Show all {filtered.length} books
            </button>
          )}
          {showAll && filtered.length > 5 && (
            <button className="reading-show-more-btn" onClick={() => setShowAll(false)}>
              Show fewer
            </button>
          )}
        </>
      )}

      {/* Import modal */}
      {importOpen && (
        <div className="import-modal-overlay" onClick={e => { if (e.target === e.currentTarget) setImportOpen(false) }}>
          <div className="import-modal">
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.5rem' }}>
              <button
                onClick={() => setImportOpen(false)}
                style={{ background: 'none', border: 'none', fontSize: '1rem', color: 'var(--stone)', cursor: 'pointer', padding: '2px 6px' }}
              >
                ✕
              </button>
            </div>
            <BookImport />
          </div>
        </div>
      )}
    </div>
  )
}

// ── Reviews ───────────────────────────────────────────────────────────────────

const REVIEW_EMOJIS = ['💭', '⭐', '❤️', '📖', '✨', '🎯', '😭', '🔥', '🌿', '💔', '🌟', '🎭']

function ReviewCard({ review, book, onUpdate, onDelete }) {
  const [editing,         setEditing]         = useState(false)
  const [draft,           setDraft]           = useState(review.body)
  const [draftEmoji,      setDraftEmoji]      = useState(review.emoji || '')
  const [showEmoji,       setShowEmoji]       = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  function save() {
    if (!draft.trim()) return
    onUpdate(review.id, { body: draft.trim(), emoji: draftEmoji || null })
    setEditing(false)
    setShowEmoji(false)
  }

  function cancelEdit() {
    setDraft(review.body)
    setDraftEmoji(review.emoji || '')
    setEditing(false)
    setShowEmoji(false)
  }

  const fs = { fontSize: '0.82rem', padding: '7px 10px', border: '1px solid rgba(0,0,0,0.15)', borderRadius: 8, background: 'var(--bg)', color: 'var(--ink)', fontFamily: 'var(--font-serif)', width: '100%', boxSizing: 'border-box', resize: 'vertical', lineHeight: 1.65 }

  return (
    <div className={`review-card-v2${review.is_archived ? ' archived' : ''}`}>
      {!editing ? (
        <>
          {review.emoji && <span className="review-card-emoji">{review.emoji}</span>}
          <p className="review-card-body">{review.body}</p>
          <div className="review-card-meta">
            <span className="review-card-book-title">{book?.title || 'Unknown book'}</span>
            {book?.author && <span className="review-card-book-author"> · {book.author}</span>}
          </div>
          <div className="review-card-actions">
            <button onClick={() => setEditing(true)}>Edit</button>
            {!review.is_archived
              ? <button onClick={() => onUpdate(review.id, { is_archived: 1 })}>Archive</button>
              : <button onClick={() => onUpdate(review.id, { is_archived: 0 })}>Unarchive</button>
            }
            <button className="delete-btn" onClick={() => setShowDeleteConfirm(true)}>Delete</button>
          </div>
        </>
      ) : (
        <div className="review-card-edit">
          <div className="review-emoji-row">
            <button className="emoji-trigger" onClick={() => setShowEmoji(v => !v)}>
              {draftEmoji || '💭'}
            </button>
            <span style={{ fontSize: '0.72rem', color: 'var(--stone)' }}>
              {draftEmoji ? 'Change emoji' : 'Add emoji'}
            </span>
            {showEmoji && (
              <div className="emoji-picker-mini">
                {REVIEW_EMOJIS.map(e => (
                  <button key={e} onClick={() => { setDraftEmoji(e); setShowEmoji(false) }}>{e}</button>
                ))}
                <button onClick={() => { setDraftEmoji(''); setShowEmoji(false) }} style={{ fontSize: '0.65rem', color: 'var(--stone)' }}>none</button>
              </div>
            )}
          </div>
          <textarea value={draft} onChange={e => setDraft(e.target.value)} rows={4} style={fs} />
          <div className="review-edit-actions">
            <button onClick={cancelEdit} style={{ fontSize: '0.78rem', padding: '4px 12px', borderRadius: 7, border: '1px solid rgba(0,0,0,0.12)', background: 'transparent', color: 'var(--stone)', cursor: 'pointer', fontFamily: 'var(--font-sans)' }}>
              Cancel
            </button>
            <button
              onClick={save}
              disabled={!draft.trim()}
              style={{ fontSize: '0.78rem', padding: '4px 12px', borderRadius: 7, border: 'none', background: draft.trim() ? 'var(--accent)' : 'var(--surf)', color: draft.trim() ? 'white' : 'var(--stone)', cursor: draft.trim() ? 'pointer' : 'default', fontFamily: 'var(--font-sans)' }}
            >
              Save
            </button>
          </div>
        </div>
      )}
      <ConfirmDialog
        open={showDeleteConfirm}
        title="Delete this review?"
        message="This cannot be undone."
        onConfirm={() => { setShowDeleteConfirm(false); onDelete(review.id) }}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </div>
  )
}

function ReviewsSection({ books }) {
  const [reviews,       setReviews]       = useState([])
  const [selectedId,    setSelectedId]    = useState('')
  const [draft,         setDraft]         = useState('')
  const [draftEmoji,    setDraftEmoji]    = useState('')
  const [showEmoji,     setShowEmoji]     = useState(false)
  const [showArchived,  setShowArchived]  = useState(false)

  useEffect(() => {
    api.getBookReviews().then(r => setReviews(r || []))
  }, [])

  const bookMap    = Object.fromEntries(books.map(b => [String(b.id), b]))
  const reviewable = books.filter(b => b.status !== 'wishlist')
  const active     = reviews.filter(r => !r.is_archived)
  const archived   = reviews.filter(r => r.is_archived)

  function addReview() {
    const book = books.find(b => String(b.id) === selectedId)
    if (!book || !draft.trim()) return
    const entry = { book_id: book.id, body: draft.trim(), emoji: draftEmoji || null }
    api.saveBookReview(entry).then(res => {
      const newReview = { ...entry, id: res?.id || Date.now(), is_archived: 0, created_at: new Date().toISOString() }
      setReviews(prev => [newReview, ...prev])
    })
    setSelectedId('')
    setDraft('')
    setDraftEmoji('')
    setShowEmoji(false)
  }

  function updateReview(id, updates) {
    setReviews(prev => prev.map(r => r.id === id ? { ...r, ...updates } : r))
    api.updateBookReview(id, updates)
  }

  function deleteReview(id) {
    setReviews(prev => prev.filter(r => r.id !== id))
    api.deleteBookReview(id)
  }

  const fs = { fontSize: '0.82rem', padding: '6px 9px', border: '1px solid rgba(0,0,0,0.15)', borderRadius: 8, background: 'var(--bg)', color: 'var(--ink)', fontFamily: 'var(--font-sans)', width: '100%', boxSizing: 'border-box' }

  return (
    <div>
      {active.length === 0 ? (
        <p style={{ fontSize: '0.88rem', color: 'var(--stone)', fontStyle: 'italic', fontFamily: 'var(--font-serif)', lineHeight: 1.75, marginBottom: '1.5rem' }}>
          No reviews yet. Add your first one below.
        </p>
      ) : (
        <div className="reviews-list">
          {active.map(r => (
            <ReviewCard
              key={r.id}
              review={r}
              book={bookMap[String(r.book_id)]}
              onUpdate={updateReview}
              onDelete={deleteReview}
            />
          ))}
        </div>
      )}

      {archived.length > 0 && (
        <>
          <button className="review-archived-toggle" onClick={() => setShowArchived(v => !v)}>
            {showArchived ? '▴' : '▾'} {archived.length} archived {archived.length === 1 ? 'review' : 'reviews'}
          </button>
          {showArchived && (
            <div className="reviews-archived">
              {archived.map(r => (
                <ReviewCard
                  key={r.id}
                  review={r}
                  book={bookMap[String(r.book_id)]}
                  onUpdate={updateReview}
                  onDelete={deleteReview}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Write a review */}
      {reviewable.length > 0 && (
        <div className="write-review-form" style={{ marginTop: '1.5rem' }}>
          <div className="challenge-section-label">Write a review</div>
          <select value={selectedId} onChange={e => setSelectedId(e.target.value)} style={{ ...fs, marginBottom: '0.6rem' }}>
            <option value="">Select a book...</option>
            {reviewable.map(b => <option key={b.id} value={b.id}>{b.title}</option>)}
          </select>
          {selectedId && (
            <>
              <div className="review-emoji-row" style={{ marginBottom: '0.5rem' }}>
                <button className="emoji-trigger" onClick={() => setShowEmoji(v => !v)}>
                  {draftEmoji || '💭'}
                </button>
                <span style={{ fontSize: '0.72rem', color: 'var(--stone)' }}>
                  {draftEmoji ? 'Change emoji' : 'Add emoji (optional)'}
                </span>
                {showEmoji && (
                  <div className="emoji-picker-mini">
                    {REVIEW_EMOJIS.map(e => (
                      <button key={e} onClick={() => { setDraftEmoji(e); setShowEmoji(false) }}>{e}</button>
                    ))}
                    <button onClick={() => { setDraftEmoji(''); setShowEmoji(false) }} style={{ fontSize: '0.65rem', color: 'var(--stone)' }}>none</button>
                  </div>
                )}
              </div>
              <textarea
                value={draft}
                onChange={e => setDraft(e.target.value)}
                placeholder="Your thoughts..."
                rows={3}
                style={{ ...fs, resize: 'vertical', fontFamily: 'var(--font-serif)', lineHeight: 1.65, marginBottom: '0.6rem' }}
              />
              <button
                onClick={addReview}
                disabled={!draft.trim()}
                style={{ background: draft.trim() ? 'var(--accent)' : 'var(--surf)', color: draft.trim() ? 'white' : 'var(--stone)', border: 'none', borderRadius: 8, padding: '6px 16px', fontSize: '0.82rem', cursor: draft.trim() ? 'pointer' : 'default', fontFamily: 'var(--font-sans)', opacity: draft.trim() ? 1 : 0.5 }}
              >
                Save review
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}

// ── Main Reading page ─────────────────────────────────────────────────────────

export default function Reading() {
  const [tab, setTab] = useState(() => {
    const p = new URLSearchParams(window.location.search)
    const t = p.get('tab')
    return TABS.find(x => x.id === t) ? t : 'library'
  })
  const [books,        setBooks]        = useState([])
  const [selectedBook, setSelectedBook] = useState(null)
  const [sessionBook,  setSessionBook]  = useState(null)

  useEffect(() => {
    api.getBooks().then(rows => setBooks(rows || []))
  }, [])

  function goTab(id) {
    setTab(id)
    history.replaceState(null, '', `?tab=${id}`)
  }

  function addBook(book) {
    const next = [book, ...books]
    setBooks(next)
    api.saveBook(book)
  }

  function updateBook(id, updates) {
    const next = books.map(b => b.id === id ? { ...b, ...updates } : b)
    setBooks(next)
    api.updateBook(id, updates)
    if (selectedBook?.id === id) setSelectedBook(prev => ({ ...prev, ...updates }))
  }

  function deleteBook(id) {
    setBooks(prev => prev.filter(b => b.id !== id))
    api.deleteBook(id)
  }

  function handleSessionSaved(session) {
    if (session.progress_value && session.progress_unit === 'pages' && sessionBook) {
      updateBook(sessionBook.id, {
        pages_read: (sessionBook.pages_read || 0) + session.progress_value
      })
    }
  }

  return (
    <div className="page active" id="page-reading">
      <div className="page-inner">

        <div className="hero">
          <p className="hero-eyebrow">My Life · Reading</p>
          <h1>Books &amp; Stories</h1>
          <hr className="hero-rule" />
          <SectionWelcome text="Your library, your pace. Every book you read is a small world you carried with you." />
        </div>

        <div className="cooking-tabs">
          {TABS.map(t => (
            <button
              key={t.id}
              className={`cooking-tab${tab === t.id ? ' active' : ''}`}
              onClick={() => goTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'library' && (
          <LibrarySection
            books={books}
            onOpenBook={setSelectedBook}
            onAddBook={addBook}
            onUpdateBook={updateBook}
          />
        )}

        {tab === 'challenge' && (
          <ReadingChallenge books={books} />
        )}

        {tab === 'suggestions' && (
          <ReadingSuggestions books={books} />
        )}

        {tab === 'reviews' && (
          <ReviewsSection books={books} />
        )}

      </div>

      {selectedBook && (
        <BookDetailPanel
          book={selectedBook}
          onClose={() => setSelectedBook(null)}
          onLogSession={() => setSessionBook(selectedBook)}
          onUpdateBook={updateBook}
          onDeleteBook={deleteBook}
        />
      )}

      {sessionBook && (
        <ReadingSessionModal
          book={sessionBook}
          onClose={() => setSessionBook(null)}
          onSaved={handleSessionSaved}
        />
      )}
    </div>
  )
}
